import React, { useState, useEffect } from 'react';
import { getFromStorage, saveToStorage, clearSecureStorage } from '../utils/storage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Container, Box, Typography, Card, CardContent, Button, AppBar, Toolbar,
  CircularProgress, Chip, Stack, Grid, styled
} from '@mui/material';
import {
  Wallet, ContentCopy, ExitToApp, Hub, DeveloperMode, BugReport, Rocket,
  CheckCircle, Circle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Styled card component matching Dashboard
const DashboardCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#000000',
  border: '1px solid #FFFFFF',
  borderRadius: 0,
  boxShadow: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 0 20px rgba(255, 255, 255, 0.1)',
  }
}));

const networkConfigs = {
  devnet: {
    name: 'AVO Devnet',
    description: 'Development Network',
    chainId: 31337,
    rpcUrl: 'http://127.0.0.1:8545',
    icon: DeveloperMode,
    color: '#4CAF50',
    status: 'active'
  },
  testnet: {
    name: 'AVO Testnet',
    description: 'Test Network',
    chainId: 1337,
    rpcUrl: 'http://testnet.avo.network:8545',
    icon: BugReport,
    color: '#FF9800',
    status: 'active'
  },
  mainnet: {
    name: 'AVO Mainnet',
    description: 'Production Network',
    chainId: 1,
    rpcUrl: 'http://mainnet.avo.network:8545',
    icon: Rocket,
    color: '#2196F3',
    status: 'coming-soon'
  },
};

function NetworkSelector({ wallet, setWallet, setIsSessionActive }) {
  const [currentNetwork, setCurrentNetwork] = useState('devnet');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadNetwork = async () => {
      const storedNetwork = await getFromStorage('currentAvoNetwork');
      if (storedNetwork && networkConfigs[storedNetwork]) {
        setCurrentNetwork(storedNetwork);
      }
    };
    loadNetwork();
  }, []);

  // Validate wallet on component mount
  useEffect(() => {
    if (!wallet?.address) {
      toast.error('No wallet connected', {
        position: 'top-center',
        style: { backgroundColor: '#000000', color: '#ffffff', border: '1px solid #FFFFFF' },
        onClose: () => navigate('/login')
      });
    }
  }, [wallet, navigate]);

  const switchNetwork = async (network) => {
    if (!networkConfigs[network]) {
      toast.error('Unsupported network', {
        position: 'top-center',
        style: { backgroundColor: '#000000', color: '#ffffff', border: '1px solid #FFFFFF' },
      });
      return;
    }

    if (networkConfigs[network].status === 'coming-soon') {
      toast.info('Mainnet coming soon!', {
        position: 'top-center',
        style: { backgroundColor: '#000000', color: '#ffffff', border: '1px solid #FFFFFF' },
      });
      return;
    }

    setIsLoading(true);
    try {
      await saveToStorage('currentAvoNetwork', network);
      setCurrentNetwork(network);
      
      toast.success(`Switched to ${networkConfigs[network].name}`, {
        position: 'top-center',
        style: { backgroundColor: '#000000', color: '#ffffff', border: '1px solid #FFFFFF' },
      });
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (error) {
      console.error('Error switching network:', error);
      toast.error(`Error switching network: ${error.message}`, {
        position: 'top-center',
        style: { backgroundColor: '#000000', color: '#ffffff', border: '1px solid #FFFFFF' },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await saveToStorage('sessionActive', 'false');
      setWallet(null);
      setIsSessionActive(false);
      toast.success('Logged out successfully', {
        position: 'top-center',
        style: { backgroundColor: '#000000', color: '#ffffff', border: '1px solid #FFFFFF' },
        onClose: () => navigate('/login')
      });
    } catch (err) {
      console.error('DEBUG: Error logging out:', err);
      toast.error('Error logging out', {
        position: 'top-center',
        style: { backgroundColor: '#000000', color: '#ffffff', border: '1px solid #FFFFFF' },
      });
    }
  };

  if (!wallet?.address) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#000000', display: 'flex', flexDirection: 'column' }}>
      <ToastContainer 
        limit={3} 
        newestOnTop 
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        style={{ zIndex: 1400 }}
      />

      {/* Top bar */}
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: '#000000',
          borderBottom: '1px solid #FFFFFF',
          boxShadow: 'none'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 500,
            color: '#FFFFFF',
            fontSize: '1.4rem',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            AVO_STATION
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={wallet?.address ? `${wallet.address.substring(0, 6)}...${wallet.address.substring(38)}` : 'No Address'}
              clickable
              icon={<ContentCopy sx={{ color: '#FFFFFF' }} />}
              onClick={() => {
                if (wallet?.address) {
                  navigator.clipboard.writeText(wallet.address);
                  toast.success('Address copied to clipboard!', {
                    position: 'top-center',
                    style: { backgroundColor: '#000000', color: '#ffffff', border: '1px solid #FFFFFF' },
                  });
                }
              }}
              sx={{ 
                bgcolor: '#000000', 
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                fontSize: '0.85rem',
                '&:hover': { bgcolor: '#FFFFFF', color: '#000000' },
                '& .MuiChip-icon': { color: '#FFFFFF' }
              }}
            />
            
            <Button
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              DASHBOARD
            </Button>
            <Button
              onClick={handleLogout}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              LOG OUT
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Container maxWidth="lg" sx={{ px: 4, py: 4, flexGrow: 1 }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 600, 
          color: '#FFFFFF', 
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          NETWORK SELECTION
        </Typography>
        <Typography variant="body2" sx={{ color: '#999999', mb: 4 }}>
          Choose the AVO network you want to connect to
        </Typography>

        <Grid container spacing={3}>
          {Object.keys(networkConfigs).map((networkKey) => {
            const network = networkConfigs[networkKey];
            const NetworkIcon = network.icon;
            const isSelected = currentNetwork === networkKey;
            const isComingSoon = network.status === 'coming-soon';
            
            return (
              <Grid item xs={12} md={4} key={networkKey}>
                <DashboardCard
                  sx={{
                    cursor: isComingSoon ? 'not-allowed' : 'pointer',
                    opacity: isComingSoon ? 0.5 : 1,
                    borderColor: isSelected ? '#FFFFFF' : '#FFFFFF',
                    borderWidth: isSelected ? '2px' : '1px',
                    '&:hover': {
                      boxShadow: isComingSoon ? 'none' : '0 0 30px rgba(255, 255, 255, 0.2)',
                    }
                  }}
                  onClick={() => !isComingSoon && switchNetwork(networkKey)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NetworkIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />
                        {isSelected && <CheckCircle sx={{ fontSize: 20, color: '#4CAF50' }} />}
                      </Box>
                      {isComingSoon && (
                        <Chip 
                          label="COMING SOON" 
                          size="small" 
                          sx={{ 
                            bgcolor: '#000000', 
                            color: '#FFFFFF',
                            border: '1px solid #FFFFFF',
                            borderRadius: 0,
                            fontSize: '0.7rem'
                          }} 
                        />
                      )}
                    </Box>
                    
                    <Typography variant="h6" sx={{ 
                      color: '#FFFFFF', 
                      mb: 1, 
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '1.1rem'
                    }}>
                      {network.name}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: '#999999', mb: 2 }}>
                      {network.description}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 0.5,
                      p: 2,
                      bgcolor: '#000000',
                      border: '1px solid #333333'
                    }}>
                      <Typography variant="caption" sx={{ color: '#666666' }}>
                        Chain ID: <span style={{ color: '#FFFFFF' }}>{network.chainId}</span>
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666666', wordBreak: 'break-all' }}>
                        RPC: <span style={{ color: '#FFFFFF', fontSize: '0.65rem' }}>{network.rpcUrl}</span>
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <Circle sx={{ fontSize: 8, color: isComingSoon ? '#666666' : '#4CAF50' }} />
                        <Typography variant="caption" sx={{ color: isComingSoon ? '#666666' : '#4CAF50' }}>
                          {isComingSoon ? 'Offline' : 'Online'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {!isComingSoon && (
                      <Button
                        fullWidth
                        disabled={isSelected || isLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          switchNetwork(networkKey);
                        }}
                        sx={{
                          mt: 2,
                          color: isSelected ? '#000000' : '#FFFFFF',
                          bgcolor: isSelected ? '#FFFFFF' : '#000000',
                          border: '1px solid #FFFFFF',
                          borderRadius: 0,
                          py: 1,
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: '#FFFFFF',
                            color: '#000000',
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#000000',
                            color: '#666666',
                            borderColor: '#666666'
                          }
                        }}
                      >
                        {isLoading ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : 
                         isSelected ? 'CONNECTED' : 'CONNECT'}
                      </Button>
                    )}
                  </CardContent>
                </DashboardCard>
              </Grid>
            );
          })}
        </Grid>

        {/* Current Network Info */}
        <DashboardCard sx={{ mt: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ 
              color: '#FFFFFF', 
              mb: 2,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '1rem'
            }}>
              CURRENT NETWORK
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box>
                {React.createElement(networkConfigs[currentNetwork].icon, { 
                  sx: { fontSize: 40, color: '#FFFFFF' } 
                })}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                  {networkConfigs[currentNetwork].name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#999999' }}>
                  {networkConfigs[currentNetwork].description} • Chain ID: {networkConfigs[currentNetwork].chainId}
                </Typography>
              </Box>
              <Chip 
                icon={<CheckCircle />} 
                label="ACTIVE" 
                sx={{ 
                  bgcolor: '#000000',
                  color: '#4CAF50',
                  border: '1px solid #4CAF50',
                  borderRadius: 0,
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: '#4CAF50' }
                }} 
              />
            </Box>
          </CardContent>
        </DashboardCard>
      </Container>
    </Box>
  );
}

export default NetworkSelector;